import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ArrowLeft } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { barrelService } from '../../services/barrelService';
import InventoryTurnoverChart from './components/InventoryTurnoverChart';
import DemandForecastingChart from './components/DemandForecastingChart';
import GeographicAnalysisPanel from './components/GeographicAnalysisPanel';
import PerformanceKPICards from './components/PerformanceKPICards';

export default function BarrelStockAnalyticsDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  
  // Analytics data
  const [barrelOrders, setBarrelOrders] = useState([]);
  const [barrelDeposits, setBarrelDeposits] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({
    turnoverRates: [],
    demandPatterns: [],
    geographicPerformance: [],
    kpis: {}
  });
  
  // Filter states
  const [timeRange, setTimeRange] = useState('30days'); // '7days', '30days', '90days', '1year'
  const [barrelTypeFilter, setBarrelTypeFilter] = useState('all'); // 'all', 'noir_270l', 'bleu_220l'

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange, barrelTypeFilter]);

  const loadAnalyticsData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError('');
      
      const [ordersData, depositsData, statsData] = await Promise.all([
        barrelService?.getBarrelOrders(),
        barrelService?.getBarrelDeposits(),
        barrelService?.getBarrelStatistics()
      ]);
      
      setBarrelOrders(ordersData);
      setBarrelDeposits(depositsData);
      setStatistics(statsData);
      
      // Calculate analytics
      const analytics = calculateAnalytics(ordersData, depositsData, timeRange, barrelTypeFilter);
      setAnalyticsData(analytics);
    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError(err?.message || 'Erreur lors du chargement des analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const calculateAnalytics = (orders, deposits, timeRange, barrelType) => {
    const now = new Date();
    const daysMap = {
      '7days': 7,
      '30days': 30,
      '90days': 90,
      '1year': 365
    };
    const days = daysMap?.[timeRange] || 30;
    const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    
    // Filter by date and barrel type
    let filteredOrders = orders?.filter(o => new Date(o?.created_at) >= startDate);
    let filteredDeposits = deposits?.filter(d => new Date(d?.created_at) >= startDate);
    
    if (barrelType !== 'all') {
      filteredOrders = filteredOrders?.filter(o => o?.barrel_type === barrelType);
      filteredDeposits = filteredDeposits?.filter(d => d?.barrel_type === barrelType);
    }
    
    // Calculate turnover rates
    const turnoverRates = calculateTurnoverRates(filteredOrders, filteredDeposits, days);
    
    // Calculate demand patterns
    const demandPatterns = calculateDemandPatterns(filteredOrders, days);
    
    // Calculate geographic performance
    const geographicPerformance = calculateGeographicPerformance(filteredOrders, filteredDeposits);
    
    // Calculate KPIs
    const kpis = calculateKPIs(filteredOrders, filteredDeposits, orders, deposits);
    
    return {
      turnoverRates,
      demandPatterns,
      geographicPerformance,
      kpis
    };
  };

  const calculateTurnoverRates = (orders, deposits, days) => {
    // Group by week for better visualization
    const weeks = Math.ceil(days / 7);
    const turnoverData = [];
    
    for (let i = 0; i < weeks; i++) {
      const weekStart = new Date(Date.now() - (weeks - i) * 7 * 24 * 60 * 60 * 1000);
      const weekEnd = new Date(Date.now() - (weeks - i - 1) * 7 * 24 * 60 * 60 * 1000);
      
      const weekOrders = orders?.filter(o => {
        const date = new Date(o?.created_at);
        return date >= weekStart && date < weekEnd;
      });
      
      const weekDeposits = deposits?.filter(d => {
        const date = new Date(d?.created_at);
        return date >= weekStart && date < weekEnd;
      });
      
      const noirSold = weekOrders?.filter(o => o?.barrel_type === 'noir_270l')?.reduce((sum, o) => sum + (o?.quantity || 0), 0);
      const bleuSold = weekOrders?.filter(o => o?.barrel_type === 'bleu_220l')?.reduce((sum, o) => sum + (o?.quantity || 0), 0);
      const noirCollected = weekDeposits?.filter(d => d?.barrel_type === 'noir_270l' && d?.collection_status === 'collected')?.reduce((sum, d) => sum + (d?.quantity || 0), 0);
      const bleuCollected = weekDeposits?.filter(d => d?.barrel_type === 'bleu_220l' && d?.collection_status === 'collected')?.reduce((sum, d) => sum + (d?.quantity || 0), 0);
      
      turnoverData?.push({
        week: `S${i + 1}`,
        date: weekStart?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
        noirSold,
        bleuSold,
        noirCollected,
        bleuCollected,
        totalTurnover: noirSold + bleuSold + noirCollected + bleuCollected
      });
    }
    
    return turnoverData;
  };

  const calculateDemandPatterns = (orders, days) => {
    // Group by day of week to identify patterns
    const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const demandByDay = Array(7)?.fill(0)?.map((_, i) => ({
      day: dayNames?.[i],
      noir: 0,
      bleu: 0,
      total: 0
    }));
    
    orders?.forEach(order => {
      const dayOfWeek = new Date(order?.created_at)?.getDay();
      const quantity = order?.quantity || 0;
      
      if (order?.barrel_type === 'noir_270l') {
        demandByDay[dayOfWeek].noir += quantity;
      } else {
        demandByDay[dayOfWeek].bleu += quantity;
      }
      demandByDay[dayOfWeek].total += quantity;
    });
    
    return demandByDay;
  };

  const calculateGeographicPerformance = (orders, deposits) => {
    const locationMap = new Map();
    
    orders?.forEach(order => {
      const location = order?.customer_city || 'Non spécifié';
      if (!locationMap?.has(location)) {
        locationMap?.set(location, {
          location,
          orderCount: 0,
          totalRevenue: 0,
          noirQuantity: 0,
          bleuQuantity: 0,
          collectionRate: 0
        });
      }
      
      const loc = locationMap?.get(location);
      loc.orderCount++;
      loc.totalRevenue += parseFloat(order?.total_price || 0);
      
      if (order?.barrel_type === 'noir_270l') {
        loc.noirQuantity += order?.quantity || 0;
      } else {
        loc.bleuQuantity += order?.quantity || 0;
      }
    });
    
    // Calculate collection rates
    deposits?.forEach(deposit => {
      const location = deposit?.customer_city || 'Non spécifié';
      if (locationMap?.has(location) && deposit?.collection_status === 'collected') {
        const loc = locationMap?.get(location);
        loc.collectionRate++;
      }
    });
    
    return Array.from(locationMap?.values())?.sort((a, b) => b?.totalRevenue - a?.totalRevenue)?.slice(0, 10); // Top 10 locations
  };

  const calculateKPIs = (filteredOrders, filteredDeposits, allOrders, allDeposits) => {
    const totalStockValue = allOrders
      ?.filter(o => o?.status === 'delivered' || o?.status === 'filled')
      ?.reduce((sum, o) => sum + parseFloat(o?.total_price || 0), 0);
    
    const monthlyConsumption = filteredOrders?.reduce((sum, o) => sum + (o?.quantity || 0), 0);
    
    const totalStock = allOrders
      ?.filter(o => o?.status === 'delivered' || o?.status === 'filled')
      ?.reduce((sum, o) => sum + (o?.quantity || 0), 0);
    
    const averageDaysOfSupply = monthlyConsumption > 0 ? Math.round((totalStock / monthlyConsumption) * 30) : 0;
    
    const avgCostPerUnit = allOrders?.length > 0 
      ? allOrders?.reduce((sum, o) => sum + parseFloat(o?.unit_price || 0), 0) / allOrders?.length
      : 0;
    
    // Calculate period comparison (current vs previous)
    const previousPeriodOrders = allOrders?.filter(o => {
      const date = new Date(o?.created_at);
      const now = new Date();
      const daysMap = { '7days': 7, '30days': 30, '90days': 90, '1year': 365 };
      const days = daysMap?.[timeRange] || 30;
      const previousStart = new Date(now.getTime() - 2 * days * 24 * 60 * 60 * 1000);
      const previousEnd = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      return date >= previousStart && date < previousEnd;
    });
    
    const previousRevenue = previousPeriodOrders?.reduce((sum, o) => sum + parseFloat(o?.total_price || 0), 0);
    const currentRevenue = filteredOrders?.reduce((sum, o) => sum + parseFloat(o?.total_price || 0), 0);
    const revenueChange = previousRevenue > 0 ? ((currentRevenue - previousRevenue) / previousRevenue) * 100 : 0;
    
    return {
      totalStockValue,
      monthlyConsumption,
      averageDaysOfSupply,
      avgCostPerUnit,
      revenueChange
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => navigate('/centralized-barrel-inventory-management-system')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600" />
              </button>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Analytics Barriques</h1>
            </div>
            <p className="text-sm text-gray-600 ml-11">Business intelligence et prévisions de demande</p>
          </div>
          <button
            onClick={() => loadAnalyticsData()}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Actualiser</span>
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Période d'Analyse
              </label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="7days">7 derniers jours</option>
                <option value="30days">30 derniers jours</option>
                <option value="90days">90 derniers jours</option>
                <option value="1year">1 an</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de Barrique
              </label>
              <select
                value={barrelTypeFilter}
                onChange={(e) => setBarrelTypeFilter(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">Tous les types</option>
                <option value="noir_270l">Barrique Noire 270L</option>
                <option value="bleu_220l">Barrique Bleue 220L</option>
              </select>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <PerformanceKPICards kpis={analyticsData?.kpis} />

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <InventoryTurnoverChart data={analyticsData?.turnoverRates} />
          <DemandForecastingChart data={analyticsData?.demandPatterns} />
        </div>

        {/* Geographic Analysis */}
        <GeographicAnalysisPanel data={analyticsData?.geographicPerformance} />
      </div>
    </div>
  );
}